import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  useAccount,
  useConnect,
  useReadContracts,
  useSendTransaction,
  useSwitchChain,
  useWriteContract,
} from 'wagmi';
import { SwapRoute } from '@uniswap/smart-order-router';
import { CustomToken, MY_TOKEN_LIST, getRoute } from 'connectors/router';
import { erc20Abi, parseUnits } from 'viem';
import { formatAmountV2, formatCryptoBalance } from 'utils/formatAmount';
import Typo from 'consts/typography';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import { Select } from 'antd';
import { IMG_ALT } from 'consts/app';
import { platformStates } from 'redux/platformSlice';
import ArrowIcon from 'assets/icons/ArrowIcon';
import useThemes from 'hooks/useThemes';
import { isValidAmount } from './WithdrawModal';

const UniSwap = () => {
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const { currentTheme } = useThemes();
  const t = useAppTranslation();

  const { address, chainId } = useAccount();
  const { connectors, connect } = useConnect();
  const { writeContractAsync } = useWriteContract();
  const { switchChainAsync } = useSwitchChain();
  const { sendTransactionAsync } = useSendTransaction();

  const [route, setRoute] = useState<SwapRoute | null>();
  const [amount, setAmount] = useState<string>('');
  const [activeCoin, setActiveCoin] = useState<CustomToken>(MY_TOKEN_LIST[1]);
  const [price, setPrice] = useState<string>('--');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const options = MY_TOKEN_LIST.filter((x) => x.symbol !== '$TBET').map((token) => ({
    value: token.symbol,
    label: (
      <div className="flex-row items-center gap-2 h-full">
        <img src={token.logoURI} alt={IMG_ALT} className="dropdown-logo" />
        <span className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
          {token.symbol}
        </span>
      </div>
    ),
  }));

  const optionsTo = MY_TOKEN_LIST.filter((x) => x.symbol === '$TBET').map((token) => ({
    value: token.symbol,
    label: (
      <div className="flex-row items-center gap-2 h-full">
        <img src={token.logoURI} alt={IMG_ALT} className="dropdown-logo" />
        <span className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}>
          {token.symbol}
        </span>
      </div>
    ),
  }));

  const result: any = useReadContracts({
    allowFailure: true,
    contracts: [
      {
        address: activeCoin.address,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address || '0x'],
        chainId: activeCoin.chainId,
      },
      {
        address: activeCoin.address,
        abi: erc20Abi,
        functionName: 'decimals',
        chainId: activeCoin.chainId,
      },
      {
        address: activeCoin.address,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [activeCoin.address, address || '0x'],
        chainId: activeCoin.chainId,
      },
    ],
  });

  const getCryptoBalance = () => {
    if (activeCoin.address) {
      return formatCryptoBalance(result?.data?.[0]?.result, activeCoin.decimals);
    }
    return '0';
  };

  const getAllowance = () => {
    return result?.data?.[2].result?.toString() || '0';
  };

  const fetchRoute = async (value: string) => {
    const routeResult = await getRoute(
      activeCoin,
      address || '0x9412de54Bd19f15823331EBECFdF969d1503010B',
      Number(value),
    );
    setRoute(routeResult);
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    const deplayDebounce = setTimeout(() => {
      if (amount) {
        fetchRoute(amount);
      }
    }, 500);
    return () => clearTimeout(deplayDebounce);
  }, [amount]);

  useEffect(() => {
    if (route) {
      const resultPrice = Number(amount || 1) / (route ? Number(route?.quote.toFixed(8)) || 1 : 1);
      setPrice(resultPrice ? resultPrice.toFixed(8) : '--');
    } else {
      setPrice('--');
    }
  }, [route]);

  const handleClick = async () => {
    if (!address) {
      connect({ connector: getConnector() });
    } else {
      if (chainId !== activeCoin.chainId) {
        await switchChainAsync({
          chainId: activeCoin.chainId,
        });
      } else if (!Number(getAllowance()) || Number(getAllowance()) < Number(amount)) {
        const res = await writeContractAsync({
          chainId: activeCoin.chainId,
          abi: erc20Abi,
          address: activeCoin.address,
          functionName: 'approve',
          args: [
            route?.methodParameters?.to as `0x${string}`,
            parseUnits(amount, activeCoin.decimals),
          ],
        });
      } else {
        const txRes = await sendTransactionAsync({
          data: route?.methodParameters?.calldata as `0x${string}`,
          to: route?.methodParameters?.to as `0x${string}`,
          value: route?.methodParameters?.value as never,
          gas: route?.estimatedGasUsed.toBigInt(),
          gasPrice: route?.gasPriceWei.toBigInt(),
        });
        if (txRes) {
          setAmount('');
        }
      }
    }
  };

  const getConnector = () => {
    const metamask = connectors.find((x) => x.id === 'io.metamask');
    return metamask ? metamask : connectors[0];
  };

  const handleTokenSelected = (value: string) => {
    const option = MY_TOKEN_LIST.find((token) => token.symbol === value);
    option && setActiveCoin(option);
  };

  useEffect(() => {
    fetchRoute('1');
  }, [activeCoin]);

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^$|^(1000000(\.0{0,2})?|[0-9]{1,6}(\.[0-9]{0,8})?|0\.)$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const getButtonText = useMemo(() => {
    if (!address) return t('staking.form.connectWallet');
    if (chainId !== activeCoin.chainId) return t('staking.form.switchChain');
    if (!Number(getCryptoBalance()) || Number(getCryptoBalance()) < Number(amount)) {
      return t('staking.form.insufficient');
    }
    if (!Number(getAllowance()) || Number(getAllowance()) < Number(amount)) {
      return t('staking.form.approve');
    }
    return t('staking.form.buy');
  }, [address, chainId, activeCoin, amount, getCryptoBalance()]);

  const canClickableButton = useMemo(() => {
    if (!address) return true;
    if (chainId !== activeCoin.chainId) return true;
    if (!Number(getCryptoBalance()) || Number(getCryptoBalance()) < Number(amount)) {
      return false;
    }
    if (!Number(amount)) return false;
    return true;
  }, [address, chainId, activeCoin, amount, getCryptoBalance()]);

  return (
    <>
      <div className={`flex w-100 items-center swap-token__container `}>
        <div className="swap-token__coin outsite-swap">
          <span className={`primary-text-color ${Typo.p5} `}>{t('tbet.buy.from')}</span>
          <div>
            <Select
              className="antd__select w-full"
              dropdownClassName="antd__select-dropdown"
              value={activeCoin.symbol}
              onChange={handleTokenSelected}
              options={options}
            />
          </div>
          <span className={`primary-text-color ${isPhone ? Typo.p8 : Typo.p5} outsite-balance`}>
            {t('balance')}: {formatAmountV2(getCryptoBalance(), 8)}
          </span>
        </div>
        <ArrowIcon
          fill={currentTheme === 'dark' ? '#FFFFFF' : '#000000'}
          width="40px"
          height="40px"
          className="mt-20"
        />
        <div className="swap-token__coin">
          <span className={`primary-text-color ${Typo.p5} `}>{t('tbet.buy.to')}</span>
          <div>
            <Select
              suffixIcon={null}
              className="antd__select w-full"
              dropdownClassName="antd__select-dropdown"
              value={optionsTo[0].value}
              options={optionsTo}
              disabled={true}
            />
          </div>
          <span className={`primary-text-color ${isPhone ? Typo.p8 : Typo.p5} `}> </span>
        </div>
      </div>
      <h3 className={`text-center primary-text-color mt-20 ${isPhone ? Typo.p3 : Typo.p2}`}>
        1 $TBET &asymp; {price} {activeCoin.symbol}
      </h3>

      <div className="flex flex-col gap-3 items-center">
        <Fragment>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('cashier.amount')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              onChange={handleChangeAmount}
              value={amount}
              placeholder={t('placeholder.enterAmount')}
            />
          </div>

          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('tbet.buy.receiveAmount')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              className={isLoading ? 'loading-input' : ''}
              disabled={true}
              value={(amount && Number(amount) !== 0 && route?.quote.toFixed(4)) || 0}
            />
          </div>
        </Fragment>
        <div className="withdraw-balance swap-input">
          <div className="withdraw-balance__left"></div>
          <div className="withdraw-balance__right">
            <button
              disabled={!canClickableButton || isLoading}
              className={`button-generate ${!canClickableButton || isLoading ? 'disabled' : ''}`}
              type="button"
              onClick={handleClick}
            >
              {getButtonText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UniSwap;
